import { useCallback, useEffect, useRef } from "react";

import { type AskMessage, AskSSEEventSchema, type AskSSEInterviewerOutputEvent } from "@performyard/service-contracts";

import { useEditFormStore } from "../../../pages/Administration/Reviews/EditForm/useEditFormStore";

import type { FormBuilderContextSnapshot } from "../types";

import { getInterviewerNotesKey } from "../store/helpers";

import { getHeaders, getUrl } from "../../../api/services/defs/internal-api/internal-base-request";

import { extractSSEMessages, parseSSEMessage } from "../utils";

import { useAskPerformYardStore } from "../AskPerformYardStore";

// Stream timeout in milliseconds (2 minutes)
const STREAM_TIMEOUT_MS = 120_000;

type SendQueryOptions = {
	query: string;
	messages?: AskMessage[];
	agent?: "form";
};

type UseAskStreamReturn = {
	sendQuery: (options: SendQueryOptions) => Promise<void>;
	abort: () => void;
	isStreaming: boolean;
};

/** Hook to send queries and handle SSE streaming responses. */
export const useAskStream = (): UseAskStreamReturn => {
	const abortControllerRef = useRef<AbortController | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

	// Cleanup on unmount to prevent memory leaks
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			readerRef.current?.cancel();
			readerRef.current = null;
		};
	}, []);

	// Subscribe to status for isStreaming check
	const status = useAskPerformYardStore((state) => state.status);

	// Get actions via getState() to avoid stale closures in async operations
	// This is the recommended Zustand pattern for actions used in callbacks
	const getStoreActions = useCallback(() => useAskPerformYardStore.getState(), []);

	const clearTimeoutRef = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const abort = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
		clearTimeoutRef();
		readerRef.current?.cancel();
		readerRef.current = null;
		const { setStatus, setStreamStage, setStreamMessage } = getStoreActions();
		setStatus("idle");
		setStreamStage(null);
		setStreamMessage(null);
	}, [clearTimeoutRef, getStoreActions]);

	const sendQuery = useCallback(
		async ({ query, messages = [], agent }: SendQueryOptions): Promise<void> => {
			// Get fresh store state and actions to avoid stale closures
			const {
				sessionId,
				addUserMessage,
				startAssistantMessage,
				appendToAssistantMessage,
				completeAssistantMessage,
				setCompetencySuggestions,
				setReportConfig,
				setStatus,
				setStreamStage,
				setStreamMessage,
				setError,
				setGoalSuggestions,
				setFormSuggestions,
				setFormQuestionSuggestions,
				activeInterviewerHarness,
				setActiveInterviewerHarness,
				deactivateInterviewerHarness,
			} = getStoreActions();

			const HARNESS_OUTPUT_HANDLERS: {
				[HARNESS_TYPE in AskSSEInterviewerOutputEvent["harness"]]: (
					event: Extract<AskSSEInterviewerOutputEvent, { harness: HARNESS_TYPE }>,
					msgId: string
				) => void;
			} = {
				competency: (event, msgId) => setCompetencySuggestions(msgId, event.suggestions),
				reporting_analytics_report: (event, msgId) => {
					setReportConfig(msgId, event.reportConfig);
					// Submit completes the interview — clear the active harness so the next user
					// message routes through the classifier instead of re-entering the interviewer.
					deactivateInterviewerHarness();
				},
			};

			// Abort any existing request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			// Create new abort controller
			abortControllerRef.current = new AbortController();

			// Set up request timeout
			clearTimeoutRef();
			timeoutRef.current = setTimeout(() => {
				if (abortControllerRef.current) {
					abortControllerRef.current.abort();
					getStoreActions().setError("Request timed out. Please try again.");
				}
			}, STREAM_TIMEOUT_MS);

			// Add user message to store
			addUserMessage(query);

			// Clear any previous error/stream state and set thinking status
			setError(null);
			setStreamStage(null);
			setStreamMessage(null);
			setStatus("thinking");

			// Push the empty streaming assistant message NOW (not after fetch returns)
			// so the in-message "Thinking…" bubble owns the placeholder from the
			// very first frame. Without this, a separately-styled placeholder would
			// render until startAssistantMessage runs post-fetch, causing a visible
			// size/style flash when the real message bubble takes over.
			const assistantMessageId: string = startAssistantMessage();

			try {
				const url = getUrl("/v1/ai/ask/stream");
				const headers = getHeaders({ body: JSON.stringify({}) });
				const interviewNotes = activeInterviewerHarness
					? (() => {
							try {
								const raw = sessionStorage.getItem(getInterviewerNotesKey(activeInterviewerHarness));
								return raw ? JSON.parse(raw) : null;
							} catch {
								return null;
							}
						})()
					: null;

				// Pending suggestions from earlier turns — re-sent so the agent can
				// iterate without losing question-level detail.
				const priorFormSuggestions = useAskPerformYardStore
					.getState()
					.formSuggestionStates.filter((fs) => fs.status === "pending")
					.map((fs) => fs.suggestion);

				// Send the in-progress form so the agent can tailor question suggestions.
				const isOnFormBuilderPage = useAskPerformYardStore.getState().isOnFormBuilderPage;
				let currentForm: FormBuilderContextSnapshot | undefined;
				if (isOnFormBuilderPage) {
					const { name, questions } = useEditFormStore.getState();
					currentForm = {
						name,
						questions: questions.map((q) => {
							// choices is a union (string[] | RatingScaleOption[]) — flatten to string labels.
							const choices = q.choices
								?.map((c) => (typeof c === "string" ? c : (c?.name ?? "")))
								.filter((c) => c.length > 0);
							return {
								type: q.type,
								question: q.question ?? "",
								subtext: q.subtext ?? "",
								...(choices && choices.length > 0 ? { choices } : {}),
							};
						}),
					};
				}

				const contextPayload: Record<string, unknown> = {};
				if (priorFormSuggestions.length > 0) contextPayload.priorFormSuggestions = priorFormSuggestions;
				if (currentForm) contextPayload.currentForm = currentForm;

				const response = await fetch(url, {
					method: "POST",
					headers: {
						...headers,
						"Content-Type": "application/json",
					} as Record<string, string>,
					body: JSON.stringify({
						query,
						sessionId,
						messages: messages.map((m) => ({
							role: m.role,
							content: m.content,
						})),
						...(Object.keys(contextPayload).length > 0 ? { context: contextPayload } : {}),
						...(interviewNotes !== null && { interviewNotes }),
						...(activeInterviewerHarness
							? { agent: "interviewer", harness: activeInterviewerHarness }
							: agent
								? { agent }
								: {}),
					}),
					signal: abortControllerRef.current.signal,
				});

				if (!response.ok) {
					const errorData = await response.json().catch(() => ({}));
					throw new Error(errorData.message || `Request failed: ${response.statusText}`);
				}

				if (!response.body) {
					throw new Error("Response body is null");
				}

				// Read the SSE stream
				const reader = response.body.getReader();
				readerRef.current = reader;
				const decoder = new TextDecoder();
				let buffer = "";

				let streamDone = false;
				while (!streamDone) {
					const { done, value } = await reader.read();

					if (done) {
						streamDone = true;
						continue;
					}

					// Decode chunk and add to buffer
					buffer += decoder.decode(value, { stream: true });

					// Extract and process complete SSE messages
					const { messages: sseMessages, remainingBuffer } = extractSSEMessages(buffer);
					buffer = remainingBuffer;

					for (const message of sseMessages) {
						const parsed = parseSSEMessage(message);

						if (!parsed) {
							continue;
						}

						const { eventType, data } = parsed;

						const result = AskSSEEventSchema.safeParse({ ...(data as object), type: eventType });
						if (!result.success) {
							console.warn("[AskStream] Failed to parse SSE event", { eventType, error: result.error });
							continue;
						}
						const event = result.data;

						switch (event.type) {
							case "status": {
								setStreamStage(event.stage);
								setStreamMessage(event.message ?? null);
								break;
							}
							case "chunk": {
								// First chunk = transition out of "thinking". Avoid spamming
								// setStatus on every chunk; only flip if we're still in thinking.
								if (useAskPerformYardStore.getState().status === "thinking") {
									setStatus("streaming");
								}
								appendToAssistantMessage(assistantMessageId, event.text);
								break;
							}
							case "goals": {
								setGoalSuggestions(assistantMessageId, event.suggestions);
								break;
							}
							case "interview_notes": {
								try {
									sessionStorage.setItem(
										getInterviewerNotesKey(event.harness),
										JSON.stringify(event.notes)
									);
								} catch {
									// sessionStorage write failures are non-fatal
								}
								setActiveInterviewerHarness(event.harness);
								break;
							}
							case "interviewer_output": {
								(
									HARNESS_OUTPUT_HANDLERS[event.harness] as (
										event: AskSSEInterviewerOutputEvent,
										msgId: string
									) => void
								)(event, assistantMessageId);
								setStreamMessage(null);
								break;
							}
							case "exit_interviewer": {
								deactivateInterviewerHarness();
								break;
							}
							case "forms": {
								setFormSuggestions(assistantMessageId, event.suggestions);
								break;
							}
							case "formQuestions": {
								setFormQuestionSuggestions(assistantMessageId, event.suggestions);
								break;
							}
							case "done": {
								completeAssistantMessage(assistantMessageId, event.sources, event.confidence);
								clearTimeoutRef();
								reader.cancel();
								return;
							}
							case "error": {
								setError(event.message);
								clearTimeoutRef();
								reader.cancel();
								return;
							}
						}
					}
				}

				// Stream ended without a done event — the backend likely crashed
				// mid-stream. Complete the message but mark it as incomplete so
				// the UI can show a subtle indicator to the user.
				console.warn("[AskStream] Stream ended without done event");
				clearTimeoutRef();
				completeAssistantMessage(assistantMessageId, undefined, undefined, true);
			} catch (err) {
				// Don't treat abort as an error
				if (err instanceof Error && err.name === "AbortError") {
					return;
				}

				// If an error occurred while streaming (e.g. lost connection),
				// complete the in-progress message as incomplete so it doesn't
				// show the streaming cursor forever.
				completeAssistantMessage(assistantMessageId, undefined, undefined, true);

				const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
				getStoreActions().setError(errorMessage);
			} finally {
				abortControllerRef.current = null;
				clearTimeoutRef();
				readerRef.current?.cancel();
				readerRef.current = null;
			}
		},
		[clearTimeoutRef, getStoreActions]
	);

	return {
		sendQuery,
		abort,
		isStreaming: status === "thinking" || status === "streaming",
	};
};
